import type { ReactNode } from 'react';
import type { EventHandler } from 'react-joyride';
import type { RolePermission } from '../../lib/rolePermissions';
import type {
  ImpersonateLocalStorage,
  TutorialLocalStorage,
  UserLocalStorage,
} from '../../types/localStorage';

import * as Sentry from '@sentry/react';
import { useSelector } from '@xstate/store-react';
import { useCallback, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import useLocalStorageState from 'use-local-storage-state';
import useSessionStorageState from 'use-session-storage-state';

import { getEnv } from '../../lib/env';
import { createSupportIssue } from '../../lib/flotiq-client/api-helpers';
import { ResponseError } from '../../lib/flotiq-client/response-errors';
import { getMaskedOrganizationName, isUserRoleAdmin } from '../../lib/helpers';
import { RolePermissions } from '../../lib/rolePermissions';
import { pageStore } from '../../lib/store/pageStore';

import ErrorFallback from '../../components/ErrorFallback/ErrorFallback';
import Tutorial from '../../components/Tutorial/Tutorial';

import { useUser } from '../../hooks/api';
import { usePageTutorial } from '../../hooks/api/usePageTutorial';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import { ModalProvider } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';

import { fetchBrowserInfo } from '../../layout/SupportIssueModal/helpers/helpers';

const ADMIN_ROLES = ['ROLE_ADMIN', 'ROLE_HEADLESS_ADMIN'];

const WrapUserContext = ({ children }: { children?: ReactNode }) => {
  const { space } = useSelectedSpace();
  const { t } = useTranslation();
  const jwt = useToken();

  const [user] = useLocalStorageState<UserLocalStorage>('cms.user');
  const [tutorialsStorage] =
    useLocalStorageState<TutorialLocalStorage>('cms.tutorials');
  const [impersonate] =
    useSessionStorageState<ImpersonateLocalStorage>('cms.impersonate');

  const { entity: userData } = useUser(user?.data?.id);

  const isAdmin = useMemo(
    () =>
      (user?.data?.roles || []).findIndex(
        (role) => ADMIN_ROLES.indexOf(role) > -1,
      ) > -1,
    [user?.data?.roles],
  );

  const isRoleAdmin = useMemo(
    () => isUserRoleAdmin(user) && !impersonate,
    [user, impersonate],
  );

  const rolesPermissions = useMemo(() => {
    if (!userData) return;

    const spaceRoles = (userData?.headlessRoles || []).filter((role) => {
      return role.space?.id === space;
    });

    const currentPermissions: RolePermission[] = [];
    spaceRoles.forEach(({ permissions }) =>
      permissions.forEach((permission) => {
        currentPermissions.push({
          ...permission,
          ctdName: permission.contentTypeDefinition?.name,
        });
      }),
    );
    return currentPermissions;
  }, [space, userData]);

  const permissions = useMemo(
    () => new RolePermissions(rolesPermissions),
    [rolesPermissions],
  );

  const params = useMemo(() => {
    return {
      limit: 100,
      page: 1,
      hydrate: 1,
      filters: JSON.stringify({
        enabled: { type: 'equals', filter: true },
      }),
    };
  }, []);

  const options = useMemo(
    () => ({
      pause:
        !JSON.parse(getEnv('VITE_ENABLE_TUTORIAL') || 'false') ||
        tutorialsStorage?.run === false,
    }),
    [tutorialsStorage?.run],
  );

  const supportButtonHandler = async (message: string) => {
    const ok = await createSupportIssue(
      jwt || '',
      space,
      {
        content: message,
        metadata: JSON.stringify(fetchBrowserInfo()),
        files: [],
      },
      t,
    );
    if (ok) {
      toast.success(t('SupportIssueModal.Form.SuccessParagraph'));
    } else {
      toast.error(t('SupportIssueModal.Form.ErrorMessage'));
    }
  };

  const getErrorFallbackPage: Sentry.FallbackRender = (event) => (
    <ErrorFallback {...event} supportButtonClick={supportButtonHandler} />
  );

  const userContextValue = useMemo(
    () => ({
      permissions,
      isAdmin,
      isRoleAdmin,
      userStorage: user,
      baseUserEventData: {
        user_id: user?.data?.id,
        organization_id: user?.data?.organization,
        organization_name: getMaskedOrganizationName(
          user?.data?.organization_name,
        ),
        plan_id: user?.data?.limits_plan?.id,
        plan_name: user?.data?.limits_plan?.name,
        space_id: space,
        user_roles: user?.data?.roles,
      },
      userData,
    }),
    [isAdmin, isRoleAdmin, permissions, space, user, userData],
  );

  const pageId = useSelector(pageStore, (state) => state.context.id) || '';

  const [
    tutorialsData,
    tutorialsIndex,
    tutorialsRun,
    updateStep,
    tutorialErrors,
  ] = usePageTutorial(pageId, params, options);

  useEffect(() => {
    if (tutorialErrors instanceof ResponseError) {
      Sentry.captureException(new Error(`Couldn't fetch tutorial`));
    }
  }, [tutorialErrors]);

  const handleTutorialCallback = useCallback<EventHandler>(
    (data) => updateStep(data),
    [updateStep],
  );

  return (
    <Sentry.ErrorBoundary fallback={getErrorFallbackPage}>
      <UserContext value={userContextValue}>
        <ModalProvider>{children || <Outlet />}</ModalProvider>
        {tutorialsData?.length > 0 && (
          <Tutorial
            run={tutorialsRun}
            steps={tutorialsData}
            stepIndex={tutorialsIndex}
            onEvent={handleTutorialCallback}
          />
        )}
      </UserContext>
    </Sentry.ErrorBoundary>
  );
};

export default WrapUserContext;
